/**
 * Button Component - プロフェッショナルデザインシステム
 * WCAG 2.1 AA準拠・アクセシビリティ完全対応
 */

import React, { forwardRef } from 'react';
import { motion } from 'framer-motion';
import { Loader2 } from 'lucide-react';
import { tokens, semanticTokens, utils } from '../tokens';

const Button = forwardRef(({
  children,
  variant = 'primary',
  size = 'medium',
  disabled = false,
  loading = false,
  fullWidth = false,
  startIcon,
  endIcon,
  onClick,
  type = 'button',
  ariaLabel,
  className = '',
  ...props
}, ref) => {
  
  // バリアント別スタイル定義
  const variants = {
    primary: {
      bg: semanticTokens.interactive.default,
      bgHover: semanticTokens.interactive.hover,
      bgActive: semanticTokens.interactive.active,
      text: tokens.colors.text.inverse,
      border: 'transparent',
      shadow: tokens.shadows.sm,
    },
    secondary: {
      bg: tokens.colors.background.primary,
      bgHover: tokens.colors.gray[50],
      bgActive: tokens.colors.gray[100],
      text: tokens.colors.text.primary,
      border: tokens.colors.gray[300],
      shadow: tokens.shadows.sm,
    },
    outline: {
      bg: 'transparent',
      bgHover: tokens.colors.gray[50],
      bgActive: tokens.colors.gray[100],
      text: tokens.colors.text.primary,
      border: tokens.colors.gray[300],
      shadow: 'none',
    },
    ghost: {
      bg: 'transparent',
      bgHover: tokens.colors.gray[50],
      bgActive: tokens.colors.gray[100],
      text: tokens.colors.text.primary,
      border: 'transparent',
      shadow: 'none',
    },
    destructive: {
      bg: tokens.colors.error[500],
      bgHover: tokens.colors.error[600],
      bgActive: tokens.colors.error[700],
      text: tokens.colors.text.inverse,
      border: 'transparent',
      shadow: tokens.shadows.sm,
    },
  };
  
  // サイズ別スタイル定義
  const sizes = {
    small: {
      padding: `${tokens.spacing[2]} ${tokens.spacing[3]}`,
      fontSize: tokens.typography.fontSize.sm,
      iconSize: '1rem',
      minHeight: '2rem',
    },
    medium: {
      padding: `${tokens.spacing[3]} ${tokens.spacing[4]}`,
      fontSize: tokens.typography.fontSize.base,
      iconSize: '1.25rem',
      minHeight: '2.5rem',
    },
    large: {
      padding: `${tokens.spacing[4]} ${tokens.spacing[6]}`,
      fontSize: tokens.typography.fontSize.lg,
      iconSize: '1.5rem',
      minHeight: '3rem',
    },
  };
  
  const variantStyle = variants[variant];
  const sizeStyle = sizes[size];
  
  // 無効・ローディング状態の判定
  const isDisabled = disabled || loading;
  
  // スタイルオブジェクト構築
  const buttonStyles = {
    // レイアウト
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: tokens.spacing[2],
    width: fullWidth ? '100%' : 'auto',
    minHeight: sizeStyle.minHeight,
    padding: sizeStyle.padding,
    
    // 外観
    backgroundColor: isDisabled ? tokens.colors.gray[200] : variantStyle.bg,
    color: isDisabled ? tokens.colors.text.disabled : variantStyle.text,
    border: `${tokens.borders.width[1]} solid ${isDisabled ? tokens.colors.gray[200] : variantStyle.border}`,
    borderRadius: tokens.borders.radius.base,
    boxShadow: isDisabled ? 'none' : variantStyle.shadow,
    
    // フォント
    fontFamily: tokens.typography.fontFamily.sans,
    fontSize: sizeStyle.fontSize,
    fontWeight: tokens.typography.fontWeight.medium,
    lineHeight: tokens.typography.lineHeight.tight,
    textDecoration: 'none',
    whiteSpace: 'nowrap',
    
    // インタラクション
    cursor: isDisabled ? 'not-allowed' : 'pointer',
    userSelect: 'none',
    transition: `all ${tokens.transitions.duration.fast} ${tokens.transitions.timing.ease}`,
    
    // アクセシビリティ
    outline: 'none',
    
    // focus-visible疑似クラスでフォーカススタイル
    '&:focus-visible': utils.focusRing(),
    
    // ホバー状態
    '&:hover:not(:disabled)': {
      backgroundColor: variantStyle.bgHover,
      transform: 'translateY(-1px)',
      boxShadow: tokens.shadows.md,
    },
    
    // アクティブ状態
    '&:active:not(:disabled)': {
      backgroundColor: variantStyle.bgActive,
      transform: 'translateY(0)',
      boxShadow: tokens.shadows.sm,
    },
  };
  
  const handleClick = (event) => {
    if (isDisabled) {
      event.preventDefault();
      return;
    }
    onClick?.(event);
  };
  
  const iconProps = {
    size: sizeStyle.iconSize,
    'aria-hidden': true,
  };
  
  return (
    <motion.button
      ref={ref}
      type={type}
      disabled={isDisabled}
      onClick={handleClick}
      style={buttonStyles}
      className={className}
      aria-label={ariaLabel}
      role="button"
      // タブフォーカス管理
      tabIndex={isDisabled ? -1 : 0}
      // アニメーション設定
      whileTap={isDisabled ? {} : { scale: 0.98 }}
      {...props}
    >
      {/* 開始アイコン */}
      {startIcon && !loading && (
        <span style={{ display: 'flex', alignItems: 'center' }}>
          {React.cloneElement(startIcon, iconProps)}
        </span>
      )}
      
      {/* ローディングスピナー */}
      {loading && (
        <motion.span
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
          style={{ display: 'flex', alignItems: 'center' }}
        >
          <Loader2 {...iconProps} />
        </motion.span>
      )}
      
      {/* テキストコンテンツ */}
      <span style={{ 
        opacity: loading ? 0.7 : 1,
        pointerEvents: 'none',
      }}>
        {children}
      </span>
      
      {/* 終了アイコン */}
      {endIcon && !loading && (
        <span style={{ display: 'flex', alignItems: 'center' }}>
          {React.cloneElement(endIcon, iconProps)}
        </span>
      )}
      
      {/* スクリーンリーダー用のローディング状態 */}
      {loading && (
        <span style={utils.srOnly()}>
          読み込み中...
        </span>
      )}
    </motion.button>
  );
});

Button.displayName = 'Button';

export { Button };